import { defineStore } from 'pinia'
import { watch } from 'vue'

import env from '~/libs/env'

import { useBorrowerStore } from '~/store/borrower'
import { useDocumentsStore } from '~/store/documents'
import { useQuestionsStore } from '~/store/questions'
import { useRootStore } from './root'
import { useAppAnalyticsStore } from './app-analytics';
import appNameFromRoute from '~/libs/app-name-from-route'
import {useAffiliateCustomizationStore} from './affiliate-customization';
import { Connect } from 'connect-web-sdk';
import { get, debounce } from 'es-toolkit/compat'

const BANK_SEARCH_DELAY = 250
const FINICITY_STALL_MS = 60000
const FINICITY_FAILURE_ACTION = /(Error|Failure|Failed)$/

let finicityStallTimer = null
let finicityManualResolver = null
let finicityLastScreen = null
let finicityPopupOpen = false
let finicityPopupListener = null

export const useBankConnectorStore = defineStore('bankConnector', () => {
  const documentsStore = useDocumentsStore()
  const borrowerStore = useBorrowerStore()
  const questionsStore = useQuestionsStore()
  const rootStore = useRootStore()
  const appAnalyticsStore = useAppAnalyticsStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const currentRoute = useRoute()
  const { $axios } = useNuxtApp()

  async function _getBanks(borrowerId) {
    return $axios.get(`${env('apiUrl')}/banking/borrower/${borrowerId}/get-banks`)
      .then(response => {
        return get(response, 'data.data') || []
      })
      .catch(() => {
        return null
      })
  }

  async function _getStatementPullStatus(borrowerId, forceRefresh = false) {
    // 1/0, not true/false - a query string only ever carries text, and the API's boolean
    // validation rejects the literal words "true"/"false" (only accepts 1/0/'1'/'0').
    return $axios.get(`${env('apiUrl')}/borrower-portal/borrower/${borrowerId}/statement-pull-status`, {
        params: forceRefresh ? { fresh: 1 } : undefined,
      })
      .then(response => {
        return get(response, 'data.data', null)
      })
      .catch((error) => {
        // Degrading to null is deliberate - callers commit nothing and the prompting surfaces
        // stay silent. Logged because nothing else will surface it: this call is the only
        // thing that puts a status in the store on load, and both the banner and the dashboard
        // upload card show nothing without one. A failure here is indistinguishable, from the
        // borrower's side, from having no statements to chase.
        console.error('Failed to fetch statement pull status:', error)
        return null
      })
  }

  async function _getBankStatement(account, monthsBack, borrowerId) {
    const payload = JSON.stringify({
      ...account,
      months: monthsBack,
    })
    return $axios.post(`${env('apiUrl')}/banking/borrower/${borrowerId}/pull-bank-statement`, payload, { headers: { 'Content-Type': 'application/json' } })
      .then(response => {
        return response || null
      })
  }

  async function _getBankingLinkToken(borrowerId) {
    return $axios.post(`${env('apiUrl')}/banking/borrower/${borrowerId}/bank-link-token`, {})
      .then(response => {
        return get(response, 'data.data', null)
      })
      .catch(() => {
        return null
      })
  }

  async function _getLiteBankingLinkToken(institutionId) {
    return $axios.post(`${env('apiUrl')}/borrower-portal/lite-connection`, { institutionGroupId: institutionId })
      .then(response => {
        return get(response, 'data.data.url', null)
      })
      .catch(() => {
        return null
      })
  }

  async function _getFixConnectionToken(connectionId) {
    return $axios.post(`${env('apiUrl')}/borrower-portal/connections/${connectionId}/fix`, {})
      .then(response => {
        return get(response, 'data.data.url', null)
      })
      .catch(() => {
        return null
      })
  }

  async function _getBankingFixToken(bankId, borrowerId) {
    return $axios.post(`${env('apiUrl')}/banking/borrower/${borrowerId}/bank-fix-token`, {
        bankId
      })
      .then(response => {
        return get(response, 'data.data', null)
      })
      .catch(() => {
        return null
      })
  }

  async function _deleteAccount(accountId) {
    return $axios.delete(`${env('apiUrl')}/borrower-portal/accounts/${accountId}`)
      .then(response => {
        return response
      })
      .catch((error) => {
        console.error('Error deleting account:', error)
        throw error
      })
  }

  async function _getTopInstitutions(limit = 6) {
    return $axios.get(`${env('apiUrl')}/borrower-portal/institution-groups`, {
      params: { limit }
    })
      .then(response => {
        return get(response, 'data.data', [])
      })
      .catch((error) => {
        console.error('Error fetching top institutions:', error)
        return []
      })
  }

  let _searchInstitutionAbort = null

  async function _searchInstitutions(searchTerm) {
    if (_searchInstitutionAbort) {
      _searchInstitutionAbort.abort()
    }

    _searchInstitutionAbort = new AbortController()

    return $axios.get(`${env('apiUrl')}/borrower-portal/institution-groups`, {
      params: { search: searchTerm },
      signal: _searchInstitutionAbort?.signal
    })
      .then(response => {
        return get(response, 'data.data', [])
      })
      .catch((error) => {
        if (error?.code === 'ERR_CANCELED') {
          return null
        }
        console.error('Error searching institutions:', error)
        return []
      })
  }

/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/

  const banks = ref([])
  const syncBankName = ref(null)
  const syncProgress = ref(null)
  const failedIndex = ref(0)
  const failed = ref(false)
  const failReason = ref(null)
  const syncingAccountPayload = ref(null)
  const pullingBankStatement = ref(false)
  const selectedAccountId = ref(null);
  const isDocumentsFastFlowEnabled = ref(false)
  const hasMultipleBankAccounts = ref(false)
  const bankConnectorError = ref(null)
  const finicityConnectActive = ref(false)
  const finicityConnectLoading = ref(false)
  const finicityManualUploadAvailable = ref(false)
  const finicityManualUploadTrigger = ref(null)
  const connectBankAccountVisible = ref(false)
  const connectBankAccountSuccessful = ref(null)
  const documentsBreakout = ref(true)
  const embeddedFinicityEventListener = ref(null)
  const newlyConnectedAccountsCount = ref(0)
  const accountConnectionNotFound = ref(false)
  const unsupportedCheckingAccounts = ref(false)
  const hasMultipleSyncFailures = ref(false)
  const filteredBanks = ref([])
  const isSearchingBanks = ref(false)
  const topSixBanks = ref([])
  const finicitySuccessTimestamp = ref(null)
  const statementPullStatus = ref(null)

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/

  const hasBanks = computed(() => {
    return banks.value.length > 0
  })

  const bankStatementsUploadedOrBanksConnected = computed(() => {
    return banks.value.length > 0 || documentsStore.hasOfTypes([{ type: 'bankStatement', min: 3 }])
  })

  const bankStatementsUploaded = computed(() => {
    return documentsStore.documents.filter(
      (doc) => doc.category === 'bankStatement'
    )
  })

  const finicityUploads = computed(() => {
    if (!bankStatementsUploaded.value) {
      return []
    } else {
      return bankStatementsUploaded.value.filter(
        (doc) => doc.uploadSource === 'finicity'
      )
    }
  })

  const finicityActive = computed(() => {
    return finicityConnectActive.value || finicityConnectLoading.value
  })

  const allBankAccounts = computed(() => {
    let flattenedAccounts = []
    banks.value.forEach((_bank) => {
      if (_bank.accounts) {
        const accounts = _bank.accounts.reduce((acc, account) => {
          return [...acc, account]
        }, [])
        flattenedAccounts = [...flattenedAccounts, ...accounts]
      }
    })

    return flattenedAccounts
  })

  const cashInAccounts = computed(() => {
    if (!hasBanks.value) {
      return 0
    }
    return allBankAccounts.value
      .filter((account) => ['checking', 'savings'].includes(account.type))
      .reduce((sum, account) => sum + account.balance, 0)
  })

  const checkingAccounts = computed(() => {
    return banks.value.reduce((acc, curr) => {
      return acc.concat(curr.accounts.filter(account => account.type === 'checking' &&  (curr?.providerStatements ?? true) === true))
    }, [])
  })

  const normalizedCheckingAccounts = computed(() => banks.value.reduce((acc, curr) => {
    const accounts = curr.accounts ?? []
    const checkingAccounts = accounts.filter(account => account.type === 'checking')
    if (checkingAccounts.length) {
      const mappedAccounts = checkingAccounts.map(account => ({
        ...account,
        providerStatements: curr?.providerStatements ?? true
      }))
      return [...acc, ...mappedAccounts]
    }
    return acc
  }, []))

  const unableToPullStatements = computed(() => unsupportedCheckingAccounts.value === true || checkingAccounts.value.length === 0)

  const statementPullFailed = computed(() => statementPullStatus.value?.status === 'failed')

  // True if we have any account status at all - meaning pulls were already submitted.
  // Used to decide whether to proceed vs resubmit on the account list page.
  // Bank-service returns {} for accountStatuses only when no correlations exist (no pulls ever
  // submitted). Once a pull is submitted, the correlation is created immediately and bank-service
  // includes that account in accountStatuses even while the pull is still in-flight.
  const hasAnyAccountStatus = computed(() => {
    const accountStatuses = statementPullStatus.value?.accountStatuses
    return Boolean(accountStatuses && Object.keys(accountStatuses).length > 0)
  })

  // A "completed" pull can still be missing required months - treat that the
  // same as an outright failed pull.
  //
  // Deliberately does NOT require every connected account to appear in accountStatuses. A
  // borrower can connect several checking accounts and select only one of them for statement
  // pulls, so the unselected accounts never get an entry and "all accounts reported" stays
  // false forever - which would pin this to false for a borrower who genuinely needs to
  // upload. The months on file from a Finicity source are what we can actually verify.
  const statementPullNeedsManualUpload = computed(() => {
    return statementPullFailed.value || (
      normalizedCheckingAccounts.value.length > 0 &&
      statementPullStatus.value?.status === 'completed' &&
      !documentsStore.hasSyncedAllFinicityStatements
    )
  })

  const getAccountPullState = computed(() => (accountId) => {
    const accountStatus = statementPullStatus.value?.accountStatuses?.[accountId]
    if (!accountStatus) return null
    if (accountStatus.failed > 0) return 'failed'
    if (accountStatus.received >= accountStatus.expected) return 'completed'
    return 'pending'
  })

  const isAccountStillPulling = (accountId) => {
    const accountStatus = statementPullStatus.value?.accountStatuses?.[accountId]
    if (!accountStatus) return false
    if (getAccountPullState.value(accountId) === 'pending') return true
    return (accountStatus.received ?? 0) + (accountStatus.failed ?? 0) < (accountStatus.expected ?? 0)
  }

  // The single derivation behind both the page's top "Retrieving recent statements" row and the
  // portal-wide banner, so the two cannot contradict each other. Returns null to mean "we have
  // nothing to say" - the row is omitted rather than guessing at a status we were never told.
  // 'pending' wins while any reported account is still pulling, so the failure and its upload
  // prompt only appear once every account has finished.
  const statementPullRetrievalState = computed(() => {
    const allCheckingAccounts = normalizedCheckingAccounts.value
    // Only providerStatements=false means definitively can't pull. highFail accounts might still
    // succeed, and an absent/unknown value stays eligible so a bank payload that omits the field
    // (the 409 multi-bank path) can't produce a false failure.
    const eligibleAccounts = allCheckingAccounts.filter(account => account.providerStatements !== false)

    if (eligibleAccounts.some(account => isAccountStillPulling(account.accountId))) return 'pending'

    // A pull that reported complete but never produced the required months still needs a manual
    // upload, whatever the individual account entries say.
    if (statementPullNeedsManualUpload.value) return 'failed'

    // Checking accounts exist but none are eligible: positive knowledge that no pull can ever
    // happen, which is what surfaces the manual-upload prompt. Not the same as an absent result.
    if (allCheckingAccounts.length > 0 && eligibleAccounts.length === 0) return 'failed'

    // Classify through getAccountPullState so this row and the per-account cards below it can
    // never disagree about what a given status object means. A null state is an account
    // bank-service has not reported on - those are excluded from the aggregation entirely, so
    // they neither block completion nor contribute pending.
    const reportedStates = eligibleAccounts
      .map(account => getAccountPullState.value(account.accountId))
      .filter(state => state !== null)

    // No eligible account was reported on. Bank-service scopes correlations to a 24h window, so a
    // borrower returning the next day gets an empty snapshot for statements that did land. Fall
    // back to what documents tell us instead of showing a perpetual spinner.
    if (reportedStates.length === 0) {
      return documentsStore.hasSyncedAllFinicityStatements ? 'completed' : null
    }

    if (reportedStates.includes('failed')) return 'failed'
    return 'completed'
  })

  // What the two *prompting* surfaces read - the portal-wide banner and the dashboard upload
  // card. Same derivation as statementPullRetrievalState, except a no-report resolves to
  // 'failed' instead of staying null: bank-service scopes correlations to 24 hours, so
  // an empty snapshot means aged out, never started, or accounts the borrower never selected -
  // and statementPullRetrievalState only stays null when the required months are also absent.
  // Either way the statements are genuinely missing, so prompt rather than show nothing.
  //
  // Deliberately NOT wired into the bank-details progress row, which reads
  // statementPullRetrievalState directly: that row omits itself on null, and would start
  // rendering a red X plus an "Upload statements" link if it consumed this instead.
  const statementPullPromptState = computed(() => {
    // Nothing has come back from bank-service yet - say nothing rather than guess.
    if (!statementPullStatus.value) return null

    // A null accountStatuses is the api degrading because it could not reach bank-service, so
    // it has no idea whether a pull ran. bank-service itself always sends an object - empty
    // when it has no correlations to report - and that empty object is what the fallthrough
    // below is allowed to read as "nothing ran, so the statements are missing". Telling a
    // borrower their pull failed during an outage we cannot see past is a different claim
    // entirely, and not one we can support.
    if (!statementPullStatus.value.accountStatuses) return null

    // Still require a connected checking account before treating this as a real status. This
    // counts every connected checking account, not just the statement-eligible ones: a bank
    // that cannot serve statements leaves the borrower needing to upload just as much, and
    // statementPullRetrievalState already resolves that case to 'failed' for the bank-details
    // row. Keying on the eligible set here would leave that row prompting while both of these
    // surfaces stayed silent.
    if (!normalizedCheckingAccounts.value.length) return null

    return statementPullRetrievalState.value ?? 'failed'
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
  ! - - refs must be exported and cannot be private to the store as it may break SSR
  ! - - https://pinia.vuejs.org/core-concepts/#Setup-Stores5e
*/

  async function bankStatementPullRequest(accountId = null, delay = 0) {
    const borrowerId = borrowerStore.borrowerId
    const payload = accountId !== null ? JSON.stringify({accountId}) : null;
    const makeRequest = (ms) => new Promise((resolve) => {
      accountConnectionNotFound.value = false
      hasMultipleBankAccounts.value = false
      unsupportedCheckingAccounts.value = false

      setTimeout(async () => {
        const response = await $axios.post(`${env('apiUrl')}/banking/borrower/${borrowerId}/statements`, payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        }).then((response) => {
          if (response?.status === 202) {
            _setHasBusinessChecking()
          }
          return response
        }).catch((error) => {
          const statusError = error?.response?.status

          if (statusError === 404) {
            accountConnectionNotFound.value = true
          }

          if (statusError === 412) {
            unsupportedCheckingAccounts.value = true
            return
          }

          // sanitize error for logging
          let sanitizedError = null;
          try {
            sanitizedError = {
              code: error.code || null,
              data: error.config?.data || null,
              message: error.message || null,
              name: error.name || null,
              status: error.status || null,
              stack: error.stack || null,
              url: error.config?.url || null,
              method: error.config?.method || null,
            }
          } catch (err) {
            // do nothing
          }

          if (statusError === 409) {
            const data = error?.response?.data?.data

            if (!data) {
              log.error('Error - no banks in response', {
                error: sanitizedError,
                borrowerId,
                accountId
              })
              return
            }

            unsupportedCheckingAccounts.value = !_hasSupportedCheckingAccount(data)
            hasMultipleBankAccounts.value = true
            banks.value = data

            _setHasBusinessChecking()
            return
          }
          log.warning('Error making bank statements pull request', {
            error: sanitizedError,
            borrowerId,
            accountId
          })
        })

        resolve(response)
      }, ms)
    })

    const response = await makeRequest(delay)

    if (accountConnectionNotFound.value){
      return await makeRequest(2000)
    }

    return response
  }

  async function setDocumentsFastFlow() {
    const appName = appNameFromRoute(currentRoute)

    if (!appName || appName !== 'marketplace' || !affiliateCustomizationStore.isLendioTenant) {
      isDocumentsFastFlowEnabled.value = false
      return
    }
    const borrowerId = borrowerStore.borrowerId
    await appAnalyticsStore.fsTrackEvent({ name: 'Documents Fast Flow', properties: { borrowerId } })
    isDocumentsFastFlowEnabled.value = true
  }

  function setAggregatorError(value) {
    if (window && window.dreams) {
      window.dreams.setEvent('bank_connect_error', 'true')
    }

    if (value === 'resetError') {
      bankConnectorError.value = null
      return
    }
    bankConnectorError.value =
      'There has been an error connecting you with our sync services. Please upload bank statements manually.'
  }

  function clearFailedPull() {
    failedIndex.value = 0
    failReason.value = null
    failed.value = false
  }

  // Monotonic call counter for applyStatementPullStatus, plus the sequence number of the most
  // recent 'failed' commit. 'completed' awaits a document refresh below before committing, while
  // 'failed' commits with no await at all, so two accounts pulling concurrently (one completing,
  // one failing moments later) can resolve out of order: the slower 'completed' call's deferred
  // commit can land after the faster, later 'failed' commit and silently clobber it. A real
  // failure is sticky against that specific race - a 'completed' call must never overwrite a
  // 'failed' committed by a call triggered after it started, since that failure is strictly
  // newer information. Other status-kind races (e.g. 'in_progress' vs 'completed') deliberately
  // keep the existing last-commit-wins behaviour: arrival order alone is not a reliable signal of
  // which of those payloads the backend produced most recently.
  let applyStatementPullStatusSequence = 0
  let lastFailedCommitSequence = 0

  // Counts the statuses committed by something other than a REST read - i.e. Pusher pushes. A
  // REST read is a snapshot from when the server answered, while a push is an event delivered
  // after it, so a push that commits while a read is still in flight is newer than that read's
  // response. Only pushes count: another REST read committing first says nothing about whether a
  // read issued after it (e.g. the post-connect refetch) is stale.
  let pushedStatusCommits = 0

  // The single way a pull status reaches the store. Both hydration paths - the Pusher
  // subscription and the REST read - go through here so they share the document refresh.
  // hydration marks the REST read's own commit so it is not counted as a push.
  async function applyStatementPullStatus(status, { hydration = false } = {}) {
    const borrowerId = borrowerStore.borrowerId
    const sequence = ++applyStatementPullStatusSequence

    // Refresh documents before a completed status. statementPullNeedsManualUpload reads
    // "completed but hasSyncedAllFinicityStatements is false" as a failure
    if (status?.status === 'completed') {
      try {
        await documentsStore.getDocuments(true)
      } catch (error) {
        // Best-effort: the status still commits below. A stale document list can only make the
        // derived state read as needing a manual upload, which the next refresh corrects.
        console.warn('Could not refresh documents before applying statement pull status:', error)
      }
    }

    // Same staleness guard as getStatementPullStatus/getBanks below: the caller's own
    // borrowerId check (e.g. useStatementPullStatus.ts's buildSharedHandler) runs before this
    // await, not after, so a business switch mid-refresh can still land here for the outgoing
    // borrower once the incoming borrower's own reset() and re-hydration have already completed.
    if (borrowerStore.borrowerId !== borrowerId) return

    if (status?.status === 'completed' && sequence < lastFailedCommitSequence) return
    if (status?.status === 'failed') lastFailedCommitSequence = sequence

    statementPullStatus.value = status
    if (!hydration) pushedStatusCommits++
  }

  async function getStatementPullStatus(forceRefresh = false) {
    const borrowerId = borrowerStore.borrowerId
    if (!borrowerId) return
    const pushedBefore = pushedStatusCommits
    const result = await _getStatementPullStatus(borrowerId, forceRefresh)
    // A borrower switch is a same-tab SPA transition, not a reload - an in-flight request from
    // the OUTGOING borrower can resolve after the switch's own force-refetch already landed.
    // Bail rather than overwrite the new borrower's data with the old borrower's (EC-15/BR-16).
    if (borrowerStore.borrowerId !== borrowerId) return
    // A push committed while this request was in flight, so this response is the older of the
    // two. Applying it would overwrite e.g. a 'failed' with a stale 'in_progress', and with no
    // polling fallback the borrower would stay on that stale state until a reload.
    if (pushedStatusCommits !== pushedBefore) return
    if (result) {
      await this.applyStatementPullStatus(result, { hydration: true })
    }
  }

  function setFailedPull(value = {}) {
    let index, _failReason
    if (value instanceof Object) {
      index = value.index
      _failReason = value.failReason
    } else {
      index = value
      _failReason = null
    }
    failedIndex.value = index
    failReason.value = _failReason
    failed.value = true
  }

  // silent: for best-effort background hydration that no screen asked for (a borrower switch,
  // the layouts). A failure there must not raise the connect flow's user-facing "error connecting
  // you with our sync services" message (bankConnectorError, which a later successful call never
  // clears) or its bank_connect_error event for a borrower who may never open a bank-connect
  // screen. banks still falls back to [] so a stale list is never kept.
  async function getBanks(force = false, { silent = false } = {}) {
    if (isDocumentsFastFlowEnabled.value === true && hasBanks.value === true && !force) {
      return
    }

    const borrowerId = borrowerStore.borrowerId
    if (!borrowerId) return

    const _banks = await _getBanks(borrowerId)
    // Same staleness guard as getStatementPullStatus above: don't let a request made for the
    // outgoing borrower land after a switch and overwrite the new borrower's own fetch.
    if (borrowerStore.borrowerId !== borrowerId) return
    if (_banks === null && !silent) {
      this.setAggregatorError()
    }
    // A failed background refresh has nothing to say about banks we already loaded: wiping them
    // would silently turn off everything derived from them (e.g. the failed-pull banner) until a
    // reload. A non-silent caller (the connect flow) still resets to empty, as it always has.
    if (_banks === null && silent) return
    banks.value = _banks || []
  }

  async function deleteAccount(accountId) {
    try {
      const response = await _deleteAccount(accountId)

      // Remove the deleted account from the banks state
      banks.value = banks.value.map(bank => {
        if (bank.accounts && Array.isArray(bank.accounts)) {
          return {
            ...bank,
            accounts: bank.accounts.filter(account => account.accountId !== accountId)
          }
        }
        return bank
      }).filter(bank => !bank.accounts || bank.accounts.length > 0)

      return response
    } catch (error) {
      console.error('Failed to delete account:', error)
      throw error
    }
  }

  async function getTopInstitutions() {
    try {
      const institutions = await _getTopInstitutions(6)
      topSixBanks.value = institutions
      return institutions
    } catch (error) {
      console.error('Failed to fetch top institutions:', error)
      topSixBanks.value = []
      return []
    }
  }

  async function openFixModal(bankId = null) {
    const borrowerId = borrowerStore.borrowerId
    bankId = bankId || get(syncingAccountPayload.value, 'bankId')
    if (!bankId) {
      console.error('BankId not found for Finicity account fix modal')
      this.clearFailedPull()
      return
    }
    const modalUrl = await _getBankingFixToken(
      bankId,
      borrowerId
    )
    if (!modalUrl) {
      console.error('Fix modal url not retrieved correctly')
      this.clearFailedPull()
      return
    }
    return await this.openFinicityModal({ modalUrl })
  }

  async function openFinicityModal({ modalUrl = null } = {}) {
    appAnalyticsStore.fsTrackEvent({name: 'Deprecated Finicity Connect'})
    connectBankAccountSuccessful.value = false
    finicityConnectLoading.value = true
    setAggregatorError('resetError') // Reset the error message since we're retrying to connect
    const borrowerId = borrowerStore.borrowerId
    const token =
      modalUrl ||
      (await _getBankingLinkToken(borrowerId))
    if (!token) {
      finicityConnectLoading.value = false
      connectBankAccountSuccessful.value = false
      setAggregatorError()
      return
    }

    const promise = new Promise((resolve, reject) => {
      if (!rootStore.isEmbedded) {
          finicityConnectActive.value = true
          window.finicityConnect.launch(token, {
            selector: '#sunrise-finicity-container',
            overlay: 'rgba(255,255,255, 0)',
            success: (event) => {
              resolve('success')
              finicitySuccess()
            },
            cancel: (event) => {
              resolve('cancel')
              finicityCancel()
            },
            error: (error) => {
              resolve('error')
              finicityError()
            },
            loaded: () => {},
            route: (event) => {
              _handleFinicityRouteEvent(event)
            },
            user: (event) => {
              _handleFinicityUserEvent(event)
            }
          })
        } else {
          // EMBEDDED ONLY FINICITY LOGIC
          // in the embedded use case, the embedded app must handle the launching of finicity
          // in order to avoid issues with loading the finicity iframe within another iframe
          window.parent.postMessage({
            source: 'BP',
            method: 'showFinicityModal',
            action: 'showFinicityModal',
            data: JSON.parse(JSON.stringify({token}))
          }, '*')
          embeddedFinicityEventListener.value = (event) => {
            if (event.data.source === 'Embedded App' && event.data.action.startsWith('FINICITY')) {
              switch (event.data.action) {
                case 'FINICITY_SUCCESS':
                  this.finicitySuccess()
                  resolve('success')
                  break
                case 'FINICITY_ERROR':
                  this.finicityError()
                  resolve('error')
                  break
                case 'FINICITY_CANCEL':
                  this.finicityCancel()
                  resolve('cancel')
                  break
                default:
                  this.finicityCancel()
                  resolve('cancel')
                  break;
              }
            }
          }
          window.addEventListener('message', embeddedFinicityEventListener.value)
        }
      })
      return await promise
    }

    async function launchFinicityConnection(connectUrl = null) {
      const borrowerId = borrowerStore.borrowerId
      const token = connectUrl || (await _getBankingLinkToken(borrowerId))

      if (!token) {
        finicityConnectLoading.value = false
        connectBankAccountSuccessful.value = false
        await setAggregatorError()
        return
      }

      appAnalyticsStore.fsTrackEvent({name: 'Finicity Connect SDK'})
      console.log('Finicity Connect SDK')

      _resetFinicityManualOffer()

      return new Promise((resolve, reject) => {
        finicityConnectActive.value = true
        const isEmbedded = rootStore.isEmbedded === true

        if (isEmbedded) {
          // EMBEDDED ONLY FINICITY LOGIC
          // in the embedded use case, the embedded app must handle the launching of finicity
          // in order to avoid issues with loading the finicity iframe within another iframe
          window.parent.postMessage({
            source: 'BP',
            method: 'showFinicityConnect',
            action: 'showFinicityConnect',
            data: JSON.parse(JSON.stringify({token}))
          }, '*')

          embeddedFinicityEventListener.value = (event) => {
            if (event.data.source === 'Embedded App' && event.data.action.startsWith('FINICITY')) {
              switch (event.data.action) {
                case 'FINICITY_SUCCESS':
                  finicitySuccess()
                  resolve('success')
                  break
                case 'FINICITY_ERROR':
                  finicityError()
                  resolve('error')
                  break
                case 'FINICITY_CANCEL':
                  finicityCancel()
                  resolve('cancel')
                  break
                default:
                  finicityCancel()
                  resolve('cancel')
                  break;
              }
            }
          }
          window.addEventListener('message', embeddedFinicityEventListener.value)
        } else {
          try {
            const connectOrigin = new URL(token).origin
            Connect.launch(
              token,
              {
                onDone: (_) => {
                  resolve('success')
                  finicitySuccess()
                },
                onCancel: (_) => {
                  resolve('cancel')
                  finicityCancel()
                },
                onError: (event) => {
                  resolve('error')
                  finicityError()
                },
                onRoute: (event) => {
                  _handleFinicityRouteEvent(event)
                },
                onUser: (event) => {
                  _handleFinicityUserEvent(event)
                },
                onLoad: () => {
                }
              },
              {
                selector: '#sunrise-finicity-container',
                overlay: 'rgba(255,255,255, 0)',
                redirectUrl: window.location.origin + window.location.pathname,
              }
            )
            _trackFinicityPopup(connectOrigin)
          } catch (e) {
            console.error('Finicity Connect launch failed', e)
            resolve('error')
            finicityError()
            return
          }
          finicityManualResolver = resolve
          _armFinicityStallTimer()
        }
      })
    }

  async function launchBankAggregatorModal(bank = null) {
    connectBankAccountSuccessful.value = false
    finicityConnectLoading.value = true
    setAggregatorError('resetError')

    // TODO add logic for other aggregators here
    // const aggregator = getAggregatorFromBank(bank)
    // if (aggregator !== 'finicity') {
    //    continue with finicity
    // } else if (aggregator === 'other-aggregator') {
    //    launch other aggregator modal
    // }
    // etc.

    let connectUrl = null
    if (bank && bank.id) {
      connectUrl = await _getLiteBankingLinkToken(bank.id)
      if (!connectUrl) {
        finicityConnectLoading.value = false
        connectBankAccountSuccessful.value = false
        await setAggregatorError()
        return
      }
    }

    return launchFinicityConnection(connectUrl)
  }

  async function launchFixConnectionModal(account) {
    connectBankAccountSuccessful.value = false
    finicityConnectLoading.value = true
    setAggregatorError('resetError')

    let fixUrl = null
    if (account && account.bankId) {
      fixUrl = await _getFixConnectionToken(account.bankId)
    }
    if (!fixUrl) {
      finicityConnectLoading.value = false
      connectBankAccountSuccessful.value = false
      await setAggregatorError()
      return
    }

    return launchFinicityConnection(fixUrl)
  }

  function finicitySuccess() {
    _resetFinicityManualOffer()
    if (!isDocumentsFastFlowEnabled.value) {
      getBanks()
    }
    finicityConnectActive.value = false
    connectBankAccountSuccessful.value = true
    finicitySuccessTimestamp.value = Date.now()
    if (window.dreams) {
      window.dreams.setEvent('bank_connect_success', 'true')
    }
    finicityConnectLoading.value = false
  }

  function finicityError() {
    _resetFinicityManualOffer()
    finicityConnectActive.value = false
    // bank_connect_error set to true in mutation below.
    setAggregatorError()
    finicityConnectLoading.value = false
  }

  function finicityCancel() {
    _resetFinicityManualOffer()
    finicityConnectActive.value = false
    if (window.dreams) {
      window.dreams.setEvent('bank_connect_cancel', 'true')
    }
    finicityConnectLoading.value = false
  }

  function finicityChooseManualUpload() {
    if (!finicityManualUploadAvailable.value) return

    appAnalyticsStore.fsTrackEvent({ name: 'Bank Connect Manual Upload Clicked', properties: { source: 'finicity-modal' } })
    const resolve = finicityManualResolver
    Connect.destroy()
    _closeFinicityPopup()
    finicityConnectActive.value = false
    finicityConnectLoading.value = false
    _resetFinicityManualOffer()
    resolve?.('manual')
  }

  function bankingConnectDestroy() {
    _resetFinicityManualOffer()
    Connect.destroy()
    finicityCancel()

    if (embeddedFinicityEventListener.value) {
      window.removeEventListener('message', embeddedFinicityEventListener.value)
      embeddedFinicityEventListener.value = null
    }
  }


  async function getBankStatements(payload = null) {
    // A payload implies this is a fresh bank statement request
    if (payload) {
      clearFailedPull()
    }
    if (!payload && !syncingAccountPayload.value) {
      return undefined
    } else if (!payload) {
      payload = syncingAccountPayload.value
    } else {
      syncingAccountPayload.value = payload
    }

    const borrowerId = borrowerStore.borrowerId

    syncProgress.value = 0
    pullingBankStatement.value = true
    const { bankId, selectedAccountId, monthsBack } = payload

    const selectedBank = banks.value.find((_bank) => _bank.bankId === bankId)
    if (!selectedBank) {
      return 'failed'
    }
    syncBankName.value = selectedBank.bankName

    const account = selectedBank.accounts.find((account) => account.accountId === selectedAccountId)

    if (!account) {
      return 'failed'
    }

    let documentModels = []
    const progressBase = monthsBack * 2
    let progressValue = failedIndex.value ? failedIndex.value * 2 - 1 : 0
    for (let index = failedIndex.value || 1; index <= monthsBack; index++) {
      let response
      if (!failedIndex.value) {
        progressValue++
      }
      syncProgress.value = Math.round((progressValue / progressBase) * 100)
      try {
        response = await _getBankStatement(
          account,
          index,
          borrowerId
        )
        // Finicity Auth retry
        if (get(response, 'status') === 203) {
          clearFailedPull()
          break
        }
      } catch (error) {
        if (/mfa required/i.test(get(error.response, 'data.errors[0].message'))) {
          setFailedPull({ index, failReason: 'mfa' })
        } else {
          console.error('Error retrieving bank statement for month, ' + index, error)
          setFailedPull({ index })
        }
        break
      }
      const documentModel = get(response, 'data.data', null)
      documentsStore.addDocument(documentModel, 'finicity')
      progressValue++
      syncProgress.value = Math.round((progressValue / progressBase) * 100)
      documentModels.push(documentModel)
    }
    pullingBankStatement.value = false
  }

  function setSelectedAccountId(account) {
    selectedAccountId.value = account
  }

  // Clears the borrower-scoped data that store/business-switch.js's switchTo() never touches
  // directly. `banks` is also what MultiAggBankStatementsAppModule.vue's onMounted cache-skip
  // reads (`!bankConnectorStore.banks?.length`) - left stale, it would keep showing the outgoing
  // borrower's accounts to a component that re-mounts for the incoming one (EC-15).
  //
  // Every other ref here is just as borrower/account-scoped and left stale would risk a
  // persistently-mounted surface (the status chip, a fix/upload modal) acting on the outgoing
  // borrower's account identifiers against the new borrower's session.
  //
  // failedIndex/failed/failReason/syncBankName/syncProgress are just as borrower-scoped as the
  // rest - left stale, getBankStatements() would resume the incoming borrower's pull at the
  // outgoing borrower's failed month (via failedIndex) and show their leftover failure/progress
  // UI. clearFailedPull() already owns the first three; syncBankName/syncProgress are reset here
  // directly since no existing helper covers them.
  function reset() {
    banks.value = []
    statementPullStatus.value = null
    selectedAccountId.value = null
    syncingAccountPayload.value = null
    hasMultipleBankAccounts.value = false
    unsupportedCheckingAccounts.value = false
    accountConnectionNotFound.value = false
    hasMultipleSyncFailures.value = false
    clearFailedPull()
    syncBankName.value = null
    syncProgress.value = null
  }

  // A borrower switch must not leave this store serving the outgoing borrower's accounts or pull
  // status to a persistently-mounted chip (BR-16). previousId is null on the very first
  // hydration (logged-out -> logged-in), which is not a switch and already starts from empty
  // state, so it is excluded here the same way bank-pull-status-ui.js excludes it.
  //
  // Calls through useBankConnectorStore() rather than the local getBanks/getStatementPullStatus
  // closures directly: both call `this.otherAction(...)` internally per this file's own
  // action-calling convention (see the ACTIONS banner above), which only resolves when invoked
  // as a method off the store instance. This works because watch() (not `immediate: true`) only
  // ever fires later, after this setup() call has already returned and Pinia has merged the full
  // set of actions onto the store it registered - NOT because Pinia pre-registers a fully-formed
  // store before setup() runs (it doesn't; the object registered at that point only has Pinia's
  // own `$patch`/`$reset`/etc., not this file's actions). Do not make this watch `immediate`, and
  // do not reuse this pattern to call a store's own action synchronously during its own setup()
  // - both would resolve to that incomplete pre-setup object and throw.
  watch(() => borrowerStore.borrowerId, (id, previousId) => {
    if (previousId == null || id === previousId) return
    const self = useBankConnectorStore()
    self.reset()
    // A logout (id null) still needs the reset above, but must not fetch with a null borrowerId -
    // that fails, sets bankConnectorError via getBanks' failure path, and the next login's
    // previousId == null guard above would never re-fetch to clear it, leaking the stale error
    // into the next borrower's session.
    if (id == null) return
    // silent: this refetch runs for every borrower who switches business, in or out of the
    // bank-pull-status experiment, and no bank-connect screen asked for it - so a failed
    // request must not surface the connect flow's error message to the new borrower.
    self.getBanks(true, { silent: true })
    self.getStatementPullStatus(true)
  })

  const _debouncedBankSearch = debounce(async (value) => {
    try {
      const results = await _searchInstitutions(value)
      if (Array.isArray(results)) {
        filteredBanks.value = results
      }
    } catch (error) {
      console.error('Error searching banks:', error)
      filteredBanks.value = []
    } finally {
      isSearchingBanks.value = false
    }
  }, BANK_SEARCH_DELAY)

  function searchBanks(name) {
    const normalizedName = (name || '').trim()
    isSearchingBanks.value = Boolean(normalizedName.length)

    if (isSearchingBanks.value) {
      _debouncedBankSearch(normalizedName)
    } else {
      _debouncedBankSearch.cancel()
      filteredBanks.value = []
    }
  }

  function _resetFinicityManualOffer() {
    clearTimeout(finicityStallTimer)
    finicityStallTimer = null
    finicityManualResolver = null
    finicityLastScreen = null
    window.removeEventListener('message', finicityPopupListener)
    finicityPopupListener = null
    finicityPopupOpen = false
    finicityManualUploadAvailable.value = false
    finicityManualUploadTrigger.value = null
  }

  // Relies on connect-web-sdk 2.3.0 internals: destroy() leaves the OAuth popup open, the SDK
  // opens it as window "targetWindow" and Connect tells the page about it with these messages.
  function _trackFinicityPopup(connectOrigin) {
    finicityPopupListener = (event) => {
      if (event.origin !== connectOrigin) return
      if (event.data?.type === 'url') finicityPopupOpen = true
      if (event.data?.type === 'closePopup') finicityPopupOpen = false
    }
    window.addEventListener('message', finicityPopupListener)
  }

  function _closeFinicityPopup() {
    if (finicityPopupOpen) window.open('', 'targetWindow')?.close()
  }

  function _armFinicityStallTimer() {
    clearTimeout(finicityStallTimer)
    finicityStallTimer = setTimeout(() => _offerFinicityManualUpload('stall'), FINICITY_STALL_MS)
  }

  function _offerFinicityManualUpload(trigger) {
    if (finicityManualUploadAvailable.value || !finicityManualResolver) return

    finicityManualUploadTrigger.value = trigger
    finicityManualUploadAvailable.value = true
    clearTimeout(finicityStallTimer)
    finicityStallTimer = null
  }

  function _isFinicityFailureEvent(event) {
    return Boolean(event?.errorCode) || FINICITY_FAILURE_ACTION.test(event?.action ?? '')
  }

  function _handleFinicityRouteEvent(event) {
    const screen = event?.screen
    if (screen) {
      appAnalyticsStore.fsTrackEvent({name: 'Finicity Modal - Route', properties: {screen}})
      if (finicityManualResolver && screen !== finicityLastScreen && !finicityManualUploadAvailable.value) {
        _armFinicityStallTimer()
      }
      finicityLastScreen = screen
    }
  }

  function _handleFinicityUserEvent(event) {
    const action = event?.action
    const errorCode = event?.errorCode

    if (_isFinicityFailureEvent(event)) _offerFinicityManualUpload('failure')
    if (!action) return
    appAnalyticsStore.fsTrackEvent({ name: 'Finicity Modal - User Action', properties: { action, errorCode } })

    if (action === 'AddAccountsSuccess') {
      newlyConnectedAccountsCount.value++
    }

    if (action === 'RemoveConnectedInstitutionSuccess' && newlyConnectedAccountsCount.value > 0) {
      newlyConnectedAccountsCount.value--
    }

    if (action === 'GetInstitutionsSuccess' && event?.searchTerm !== undefined) {
      const searchTerm = event.searchTerm
      appAnalyticsStore.fsTrackEvent({ name: 'Finicity Modal - Search Term', properties: { searchTerm } })
    }
  }

  async function _setHasBusinessChecking() {
    const hbc = borrowerStore.borrowerValues?.hasBusinessChecking?.value
    if (!hbc || hbc === '0') {
      try {
        await questionsStore.saveAnswers({ answers: { hasBusinessChecking: true } })
        await borrowerStore.getBorrowerValues({ forceReload: true })
      } catch (e) {
        // non-critical
      }
    }
  }

  function _hasSupportedCheckingAccount(banks) {
    for (const bank of banks) {
      if (!Array.isArray(bank.accounts)) {
        continue;
      }
      for (const account of bank.accounts) {
        if (account.type === 'checking' && bank?.providerStatements !== false) {
          return true;
        }
      }
    }
    return false;
  }

  return {
    //STATE
    banks,
    syncBankName,
    syncProgress,
    failed,
    failedIndex,
    failReason,
    syncingAccountPayload,
    pullingBankStatement,
    bankConnectorError,
    isDocumentsFastFlowEnabled,
    hasMultipleBankAccounts,
    finicityConnectActive,
    finicityConnectLoading,
    finicityManualUploadAvailable,
    finicityManualUploadTrigger,
    connectBankAccountVisible,
    connectBankAccountSuccessful,
    documentsBreakout,
    selectedAccountId,
    newlyConnectedAccountsCount,
    accountConnectionNotFound,
    unableToPullStatements,
    unsupportedCheckingAccounts,
    hasMultipleSyncFailures,
    filteredBanks,
    isSearchingBanks,
    finicitySuccessTimestamp,
    statementPullStatus,

    //GETTERS
    hasBanks,
    bankStatementsUploadedOrBanksConnected,
    bankStatementsUploaded,
    finicityUploads,
    finicityActive,
    allBankAccounts,
    cashInAccounts,
    checkingAccounts,
    normalizedCheckingAccounts,
    statementPullFailed,
    hasAnyAccountStatus,
    statementPullNeedsManualUpload,
    statementPullPromptState,
    statementPullRetrievalState,
    getAccountPullState,
    topSixBanks,

    //ACTION
    bankingConnectDestroy,
    finicityChooseManualUpload,
    launchBankAggregatorModal,
    bankStatementPullRequest,
    setDocumentsFastFlow,
    clearFailedPull,
    finicityCancel,
    finicityError,
    finicitySuccess,
    getBanks,
    getTopInstitutions,
    deleteAccount,
    getBankStatements,
    openFinicityModal,
    openFixModal,
    setFailedPull,
    applyStatementPullStatus,
    getStatementPullStatus,
    setAggregatorError,
    setSelectedAccountId,
    searchBanks,
    launchFixConnectionModal,
    reset,
  }
})
